import { Directory, Filesystem } from '@capacitor/filesystem'
import { StoreRecapImagesRow, StoreRecapImagesRows } from '../types/database'
import { useDatabase } from '../composables/database'
import { Capacitor } from '@capacitor/core'
import { useStoreRecaps } from '../store/useStoreRecaps'
import { storeToRefs } from 'pinia'
import { logInfo } from '../utils'
import { Nullish } from '../types/utility'
import { addBreadcrumb } from '@sentry/vue'

export const recapImagesFolder = 'FHS/store_recap_response_images'

/**
 * Interacts with the recap images table and device recap images folder (mobile only)
 * @returns {StoreRecapImagesModelReturn}
 */
export const StoreRecapImagesModel = (): StoreRecapImagesModelReturn => {
  const { run, query } = useDatabase()
  const recapStore = storeToRefs(useStoreRecaps())

  async function queryAllStoreRecapImages(): Promise<StoreRecapImagesRows> {
    const sql = `
      SELECT
        *
      FROM
        store_recap_response_images
    `
    return (await query(sql)) as unknown as StoreRecapImagesRows
  }

  async function getAllStoreRecapImages(): Promise<StoreRecapImageUpload[]> {
    const imageMappings: StoreRecapImageUpload[] = []
    const sql = `
      SELECT
        *
      FROM
        store_recap_response_images
    `
    const images = (await query(sql)) as unknown as StoreRecapImagesRows
    for (const image of images) {
      const mappedImageArr = await mapImageFiles(
        [image],
        String(image.recap_id),
        String(image.store_id)
      )
      for (const mapping of mappedImageArr) {
        imageMappings.push(mapping)
      }
    }
    return imageMappings
  }

  async function getStoreRecapImagesForRecap(
    recapId: string,
    storeId: string
  ): Promise<StoreRecapImageUpload[]> {
    let images: StoreRecapImagesRows = []
    try {
      const sql = `
        SELECT
          *
        FROM
          store_recap_response_images
        WHERE
          recap_id = ${recapId}
          AND store_id = ${storeId}
      `
      images = (await query(sql)) as unknown as StoreRecapImagesRows
      return await mapImageFiles(images, recapId, storeId)
    } catch (err) {
      logInfo('error', 'StoreRecapImagesForRecap', 'Error fetching store recap images for recap', {
        err,
        images,
      })
      return []
    }
  }

  async function getStoreRecapImagesForRecapField(
    recapId: string,
    storeId: string,
    fieldInstanceId: string,
    fieldRepeatingIndex: number
  ): Promise<StoreRecapImageUpload[]> {
    let images: StoreRecapImagesRows = []
    try {
      const sql = `
        SELECT
          *
        FROM
          store_recap_response_images
        WHERE
          recap_id = ${recapId}
          AND store_id = ${storeId}
          AND field_instance_id = ${fieldInstanceId}
          AND field_repeating_index = ${fieldRepeatingIndex}
      `
      images = (await query(sql)) as unknown as StoreRecapImagesRows
      return await mapImageFiles(images, recapId, storeId, fieldInstanceId, fieldRepeatingIndex)
    } catch (err) {
      logInfo('error', 'StoreRecapImagesForField', 'Error fetching store recap images for field', {
        err,
        images,
      })
      return []
    }
  }

  async function mapImageFiles(
    images: StoreRecapImagesRows,
    recapId: string,
    storeId: string,
    fieldInstanceId?: string,
    fieldRepeatingIndex?: number
  ): Promise<StoreRecapImageUpload[]> {
    addBreadcrumb({
      level: 'debug',
      category: 'MapStoreRecapImages',
      message: 'mapStoreRecapImages',
      data: {
        images,
        recapId,
        storeId,
        fieldInstanceId,
        fieldRepeatingIndex,
      },
    })
    const imageFiles: StoreRecapImageUpload[] = []
    if (images && images.length) {
      if (Capacitor.isNativePlatform()) {
        try {
          for (const image of images) {
            const fileData = await fetchFile(image.path)
            if (fileData) {
              imageFiles.push({
                ...image,
                data: fileData,
              })
            }
          }
        } catch (error) {
          logInfo('error', 'StoreRecapImagesMap', 'Images for store could not be mapped', {
            error,
            recapId,
            storeId,
            images,
            fieldInstanceId,
            fieldRepeatingIndex,
          })
        }
      } else {
        try {
          for (const img of recapStore.recapImages.value) {
            if (
              !!img &&
              String(img.recap_id) === String(recapId) &&
              String(img.store_id) === String(storeId) &&
              (fieldInstanceId
                ? String(img.image.field_instance_id) === String(fieldInstanceId)
                : true) &&
              (fieldRepeatingIndex
                ? String(img.image.field_repeating_index) === String(fieldRepeatingIndex)
                : true)
            ) {
              imageFiles.push({
                ...img.image,
                id: Number(img.image.id),
              })
            }
          }
        } catch (error) {
          logInfo(
            'error',
            'RecapImages: Error converting store images (desktop)',
            'error converting image',
            {
              error,
            }
          )
        }
      }
    }
    return imageFiles
  }

  async function writeStoreRecapImage(
    recapId: string,
    storeId: string,
    fieldInstanceId: string,
    fieldRepeatingIndex: number,
    data: { path: string; metadata: object },
    image: string | Nullish
  ): Promise<void> {
    if (!image) {
      throw new Error('No image provided to write to device')
    }

    // Ensure the directory to store the images is created
    await makeStoreRecapImagesDir()

    const sql = `
      INSERT INTO store_recap_response_images ('date_uploaded', 'path', 'metadata', 'recap_id', 'store_id', 'field_instance_id', 'field_repeating_index')
      VALUES (time(), ?, ?, ?, ?, ?, ?);
    `
    const values: any[] = [
      data.path,
      JSON.stringify(data.metadata),
      recapId,
      storeId,
      fieldInstanceId,
      fieldRepeatingIndex,
    ]

    try {
      await run(sql, values)

      // Write the image to the device
      if (Capacitor.isNativePlatform()) {
        await Filesystem.writeFile({
          data: image,
          path: `${recapImagesFolder}/${data.path}`,
          directory: Directory.Data,
        })
      } else {
        // Add to the recap store
        recapStore.recapImages.value.push({
          recap_id: recapId,
          store_id: storeId,
          image: {
            id: 0, // The ID doesn't matter for Desktop images, so this is strictly to appease TypeScript
            data: image,
            date_uploaded: new Date().toString(),
            metadata: JSON.stringify(data.metadata),
            path: data.path,
            recap_id: Number(recapId),
            store_id: Number(storeId),
            field_instance_id: Number(fieldInstanceId),
            field_repeating_index: Number(fieldRepeatingIndex),
          },
        })
      }
    } catch (error) {
      logInfo('error', 'StoreRecapImagesWrite', 'Error writing store recap image', {
        error,
        recapId,
        storeId,
        fieldInstanceId,
        fieldRepeatingIndex,
        data,
      })
      throw new Error(`Error writing store recap image: ${error}`)
    }
  }

  // Remove any images over 14 days old
  async function removeStaleStoreRecapImages() {
    try {
      const sql = `
        SELECT * FROM store_recap_response_images WHERE date_uploaded <= date('now','-14 day'); 
    `
      const staleImages: StoreRecapImagesRows | void | null = await query(sql)
      if (staleImages) {
        for (const image of staleImages) {
          try {
            await deleteFile(image.path)
            await run('DELETE FROM store_recap_response_images WHERE id = ?;', [image.id])
          } catch (err) {
            logInfo('error', 'StoreRecapImagesDelete', 'Error deleting stale store image', { err })
          }
        }
      }
    } catch (err) {
      logInfo('error', 'StoreRecapImagesStaleDelete', 'Error removing stale store images', {
        err,
      })
    }
  }

  async function removeStoreRecapImage(path: string): Promise<boolean> {
    // Delete the image from the database
    const deleteSql = 'DELETE FROM store_recap_response_images WHERE path = ?;'
    await run(deleteSql, [path])
    return await deleteFile(path)
  }

  const removeByStoreId = async (storeId: string) => {
    const sql = `SELECT * FROM store_recap_response_images WHERE store_id = ${storeId};`
    const toDelete: StoreRecapImagesRows | void | null = await query(sql)
    if (toDelete) {
      for (const imgToDelete of toDelete) {
        try {
          await removeStoreRecapImage(imgToDelete.path)
        } catch (err) {
          console.error(`Error removing store recap image: ${err}`)
        }
      }
    }
  }

  // Capacitor only
  async function makeStoreRecapImagesDir(): Promise<void> {
    if (Capacitor.isNativePlatform()) {
      await Filesystem.mkdir({
        path: recapImagesFolder,
        directory: Directory.Data,
        recursive: true,
      }).catch((error) => {
        if (error.message !== 'Directory exists') {
          throw new Error(`Error creating store recap image directory: ${error}`)
        }
      })
    }
  }

  /**
   * Currently this method is only called from the clearQueue, and as such
   * all recap definitions should be deleted by now from the Pinia store.
   * If this ever changes, make sure to remove the images from each definition in the store here.
   */
  async function clearStoreRecapImages() {
    try {
      const sql = 'DELETE FROM store_recap_response_images'
      await run(sql, [])
      if (Capacitor.isNativePlatform()) {
        try {
          // Try to remove the original images directory
          await Filesystem.rmdir({
            path: recapImagesFolder,
            directory: Directory.Documents,
            recursive: true,
          })
        } catch (err) {
          console.error(`Error removing the original store recap images directory: ${err}`)
        }
        try {
          // Try to remove the new images directory
          await Filesystem.rmdir({
            path: recapImagesFolder,
            directory: Directory.Data,
            recursive: true,
          })
        } catch (err) {
          console.error(`Error removing the new store recap images directory: ${err}`)
        }
      }
    } catch (err) {
      logInfo('error', 'StoreRecapImagesClear', 'Error clearing store recap images', { err })
    }
  }

  async function fetchFile(
    path: string,
    directory: Directory = Directory.Data
  ): Promise<string | null> {
    let fileData: string | null = null
    try {
      const documentFile = await Filesystem.readFile({
        path: `${recapImagesFolder}/${path}`,
        directory,
      })
      if (documentFile && documentFile.data) {
        fileData = documentFile.data
      } else {
        addBreadcrumb({
          level: 'debug',
          category: 'FetchImage',
          message: 'File not found or empty',
          data: {
            path,
            directory,
          },
        })
        if (directory === Directory.Documents) {
          // We've now checked both directories and the file is missing or empty
          const err = new Error(
            `Error: File not found or file data empty! Path: ${path}, Directory: ${directory}`
          )
          logInfo('error', 'StoreRecapImagesFetch', 'Error fetching file', { err, path, directory })
          throw err
        }
      }
    } catch (err) {
      logInfo('debug', 'StoreRecapImagesDocumentsFetch', 'Error fetching file', { err })
    }

    // If we don't have the file yet, try using the Documents directory instead (if we haven't already looked)
    if (!fileData && directory !== Directory.Documents) {
      return fetchFile(path, Directory.Documents)
    }

    return fileData
  }

  async function deleteFile(path: string): Promise<boolean> {
    let deleted = false
    if (Capacitor.isNativePlatform()) {
      try {
        // First try deleting the file from the Data directory
        await Filesystem.deleteFile({
          path: `${recapImagesFolder}/${path}`,
          directory: Directory.Data,
        })
        deleted = true
      } catch (err) {
        console.error(`Error deleting file: ${err}`)
      }
      if (!deleted) {
        try {
          // Then try deleting the file from the Documents directory
          await Filesystem.deleteFile({
            path: `${recapImagesFolder}/${path}`,
            directory: Directory.Documents,
          })
          deleted = true
        } catch (err) {
          console.error(`Error deleting file: ${err}`)
        }
        if (!deleted) {
          logInfo('error', 'StoreRecapImageDelete', 'Error deleting image', { path })
        }
      }
    } else {
      const imageLength = recapStore.recapImages.value.length
      if (imageLength) {
        recapStore.recapImages.value = recapStore.recapImages.value.filter(
          (img) => img.image.path !== path
        )
        if (imageLength > recapStore.recapImages.value.length) {
          deleted = true
        }
      }
    }
    return deleted
  }

  return {
    getAllStoreRecapImages,
    getStoreRecapImagesForRecap,
    getStoreRecapImagesForRecapField,
    writeStoreRecapImage,
    removeStoreRecapImage,
    removeStaleStoreRecapImages,
    removeByStoreId,
    clearStoreRecapImages,
    queryAllStoreRecapImages,
  }
}

type StoreRecapImagesModelReturn = {
  getAllStoreRecapImages: () => Promise<StoreRecapImageUpload[]>
  getStoreRecapImagesForRecap: (
    recapId: string,
    storeId: string
  ) => Promise<StoreRecapImageUpload[]>
  getStoreRecapImagesForRecapField: (
    recapId: string,
    storeId: string,
    fieldInstanceId: string,
    fieldRepeatingIndex: number
  ) => Promise<StoreRecapImageUpload[]>
  writeStoreRecapImage: (
    recapId: string,
    storeId: string,
    fieldInstanceId: string,
    fieldRepeatingIndex: number,
    data: { path: string; metadata: object },
    image: string | Nullish
  ) => Promise<void>
  removeStoreRecapImage: (path: string) => Promise<boolean>
  removeStaleStoreRecapImages: () => Promise<void>
  removeByStoreId: (storeId: string) => Promise<void>
  clearStoreRecapImages: () => Promise<void>
  queryAllStoreRecapImages: () => Promise<StoreRecapImagesRows>
}

export type StoreRecapImageUpload = StoreRecapImagesRow & {
  data: string
}

export type FileReference = {
  id: string
  data: string
}
